import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { loadCareerBySlug } from '../utils/contentLoader';

const TYPE_META = {
  work:           { label: 'Work',            color: '#7cb8a0' },
  education:      { label: 'Education',       color: '#8a9fd4' },
  extracurricular:{ label: 'Extracurricular', color: '#c4a24a' },
};

export default function CareerDetail() {
  const { slug } = useParams();
  const entry = useMemo(() => loadCareerBySlug(slug), [slug]);

  if (!entry) {
    return (
      <div style={{ padding: '48px', maxWidth: '760px' }}>
        <Link to="/career" style={{ color: 'var(--gold)', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', textDecoration: 'none' }}>
          ← Back to Career
        </Link>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: 'var(--text)', marginTop: '24px' }}>
          Entry not found
        </h1>
        <p style={{ color: 'var(--text-3)' }}>The entry "{slug}" could not be found.</p>
      </div>
    );
  }

  const meta = TYPE_META[entry.type] || TYPE_META.work;

  return (
    <div style={{ padding: '48px', maxWidth: '820px' }}>

      {/* Back link */}
      <Link
        to="/career"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--text-3)',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.06em',
          textDecoration: 'none',
          marginBottom: '32px',
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}
      >
        ← CAREER & ED
      </Link>

      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.08em',
            color: meta.color,
            textTransform: 'uppercase',
          }}>
            {meta.label}
          </span>
          {entry.dateRange && (
            <>
              <span style={{ color: 'var(--border-2)', fontSize: '0.7rem' }}>·</span>
              <span style={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.6rem',
                color: 'var(--text-3)',
              }}>
                {entry.dateRange}
              </span>
            </>
          )}
          {entry.current && (
            <>
              <span style={{ color: 'var(--border-2)', fontSize: '0.7rem' }}>·</span>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#4ade80',
                  boxShadow: '0 0 0 2px rgba(74,222,128,0.2)',
                }} />
                <span style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.55rem',
                  color: '#4ade80',
                  letterSpacing: '0.05em',
                }}>CURRENT</span>
              </span>
            </>
          )}
        </div>

        <h1 style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 'clamp(1.8rem, 4vw, 3rem)',
          fontWeight: 700,
          letterSpacing: '-0.03em',
          lineHeight: 1,
          color: 'var(--text)',
          margin: '0 0 8px',
        }}>
          {entry.role}
        </h1>

        <p style={{
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          fontSize: '1rem',
          color: 'var(--text-2)',
          margin: '0 0 4px',
        }}>
          {entry.institution}
        </p>

        {entry.location && (
          <p style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            color: 'var(--text-3)',
            margin: 0,
          }}>
            {entry.location}
          </p>
        )}
      </div>

      {/* Skills */}
      {entry.skills && entry.skills.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '32px' }}>
          {entry.skills.map((s) => (
            <span key={s} className="tag">{s}</span>
          ))}
        </div>
      )}

      {/* Divider */}
      <div style={{ height: '1px', background: 'var(--border)', marginBottom: '40px' }} />

      {/* Prose content from markdown */}
      <div
        className="prose-detail"
        dangerouslySetInnerHTML={{ __html: entry.contentHtml }}
      />

      {/* Related links */}
      {entry.relatedLinks && entry.relatedLinks.length > 0 && (
        <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
          <div className="eyebrow" style={{ marginBottom: '14px' }}>Related</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {entry.relatedLinks.filter((link) => link && link.href).map((link) => {
              const isExternal = link.href.startsWith('http');
              const sharedStyle = {
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                fontSize: '0.78rem',
                color: 'var(--gold)',
                textDecoration: 'none',
                padding: '6px 12px',
                border: '1px solid transparent',
                borderRadius: '4px',
                background: 'var(--gold-dim)',
                transition: 'border-color 0.15s ease',
                display: 'inline-block',
              };
              if (isExternal) {
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={sharedStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                  >
                    {link.label} ↗
                  </a>
                );
              }
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  style={sharedStyle}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  {link.label} →
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom back link */}
      <div style={{ marginTop: '56px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
        <Link
          to="/career"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-3)',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.06em',
            textDecoration: 'none',
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}
        >
          ← CAREER & ED
        </Link>
      </div>
    </div>
  );
}
